import { ControlledSelectField } from 'components/Form';
import { useRequest } from 'features/request/useRequest';
import type { GlobalOtherCostData } from 'gql/graphql';
import { ProjectMethodologyEnum, SortEnumType } from 'gql/graphql';
import { SelectItem } from 'uikit';

import type React from 'react';
import { memo, useMemo } from 'react';
import type { Control } from 'react-hook-form/dist/types/form';

import { availableChapters, availableTypes } from './commonData';
import { bimGlobalOtherCostsForContractGql, uerGlobalOtherCostsForContractGql } from './createOtherQueries';

import Styles from './CreateOtherModal.module.css';

const prepareSelectItems = <I extends GlobalOtherCostData>(items: I[]): (I & { label: any; id: any })[] | undefined =>
    items.map((item) => ({
        ...item,
        label: item?.name,
        id: item?.id,
    }));

interface ICreateOtherNameSelectProps {
    control: Control;
    contractId: number;
    methodology: ProjectMethodologyEnum;
    refTooltip?: React.MutableRefObject<null>;
    onMouseMoveSelect?: () => void;
    onMouseLeaveSelect?: () => void;
    isTooltipHidden?: boolean;
}

export const CreateOtherNameSelect = memo((props: ICreateOtherNameSelectProps) => {
    const { control, contractId, refTooltip, onMouseMoveSelect, onMouseLeaveSelect, isTooltipHidden, methodology } =
        props;
    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);

    const variables = useMemo(
        () => ({
            ...(isUer ? { methodology } : {}),
            contractId: Number(contractId),
            order: [{ section: SortEnumType.Desc, name: SortEnumType.Asc }],
        }),
        [contractId, isUer, methodology],
    );

    const [uerGlobalOtherCostsData] = useRequest({
        requestId: 'globalOtherCosts',
        query: uerGlobalOtherCostsForContractGql,
        variables: variables as any,
        withEffect: isUer,
    });

    const [bimGlobalOtherCostsData] = useRequest({
        requestId: 'globalOtherCosts',
        query: bimGlobalOtherCostsForContractGql,
        variables,
        withEffect: !isUer,
    });

    const selectItems = useMemo(
        () =>
            prepareSelectItems(
                (isUer
                    ? uerGlobalOtherCostsData?.uerGlobalOtherCostsForContract?.items
                    : bimGlobalOtherCostsData?.bimGlobalOtherCostsForContract?.items) ?? [],
            ),
        [
            bimGlobalOtherCostsData?.bimGlobalOtherCostsForContract?.items,
            isUer,
            uerGlobalOtherCostsData?.uerGlobalOtherCostsForContract?.items,
        ],
    );

    return (
        <ControlledSelectField
            isTooltipHidden={isTooltipHidden}
            label="Наименование"
            name="globalOtherCostId"
            control={control}
            items={selectItems ?? []}
            required
            refTooltip={refTooltip}
            onMouseMove={onMouseMoveSelect}
            onMouseLeave={onMouseLeaveSelect}
            rules={{
                validate: (validateData) => (validateData?.id ? true : 'Обязательное поле'),
            }}
            renderItem={({ item, active, hovered, onClick, onMouseEnter }: any) => (
                <SelectItem
                    label={
                        (
                            <div
                                className={Styles.createSelectOption}
                                role="presentation"
                                onMouseEnter={onMouseEnter}
                                onClick={onClick}
                            >
                                <div className={Styles.selectTopLabel}>
                                    {availableChapters[item.chapter] ?? 'Лимиты Заказчика'} /{' '}
                                    {availableTypes[item.calculatingType] ?? '–'}
                                </div>
                                {item.label}
                            </div>
                        ) as any
                    }
                    active={active}
                    hovered={hovered}
                    multiple={false}
                    onClick={onClick}
                    onMouseEnter={onMouseEnter}
                    size="s"
                    indent="normal"
                    disabled={false}
                />
            )}
        />
    );
});

CreateOtherNameSelect.displayName = 'CreateOtherNameSelect';
